// The PC navigation rail: the left leg of the indigo L-shell (≥1024px, and only while not collapsed). It
// lists the same groups and pages as the nav menu, filtered by the same rules (useNavModel), with each
// submenu as a disclosure of indented sub-items. The colour-profile swatches sit at its foot, wired to
// the palette setting (not in a theme that brings its own colours, such as Stegi). Links are real
// <a href>s (open-in-new-tab works); Up/Down/Home/End move between rows without changing the Tab order.
// A few rows carry a count (overdue deliveries, new service requests), read only from what the React
// Query cache already holds: the rail never fetches for them.

import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { mesOverdue } from '@technohull/shared';
import type { NavIcon, NavItem } from '../app/nav.js';
import { useNavModel } from '../app/useNavModel.js';
import type { OverviewResponse, ServiceRequestsResponse } from '../features/hooks.js';
import { PALETTES, PALETTE_ORDER, paletteId } from '../lib/palettes.js';
import { useActiveSeason } from '../season/SeasonContext.js';
import { useSettings } from '../settings/SettingsContext.js';

// ── row counts, from the cache only ─────────────────────────────────────────────────────────────
export interface NavBadge { n: number; tone: 'err' | 'info'; label: string }

/** one query's cached data, without fetching it or adding an observer (so it neither triggers a request
 *  nor keeps the entry alive); re-renders when that cache entry changes, undefined until something loads it. */
function useCachedData<T>(key: readonly unknown[]): T | undefined {
  const qc = useQueryClient();
  const subscribe = useCallback((onChange: () => void) => qc.getQueryCache().subscribe(onChange), [qc]);
  return useSyncExternalStore(subscribe, () => qc.getQueryData<T>(key));
}

/** path → count for the nav rows that carry one. Nothing while the data isn't cached, nothing for a 0.
 *  Pre-Delivery Alerts: boats in the yard past their delivery date (the Overview's overdue rule) in the
 *  active season's overview. Service Requests: requests still 'new' in the service-requests list. */
export function useNavCounts(): Record<string, NavBadge> {
  const season = useActiveSeason();
  const overview = useCachedData<OverviewResponse>(['overview', season]);
  const requests = useCachedData<ServiceRequestsResponse>(['service-requests']);
  return useMemo(() => {
    const out: Record<string, NavBadge> = {};
    if (overview?.vessels) {
      const now = new Date();
      const n = overview.vessels.filter((v) => !v.left && mesOverdue(v, now)).length;
      if (n > 0) out['/assembly-alerts'] = { n, tone: 'err', label: `${n} overdue` };
    }
    if (requests?.requests) {
      const n = requests.requests.filter((r) => r.status === 'new').length;
      if (n > 0) out['/service-requests'] = { n, tone: 'info', label: `${n} new` };
    }
    return out;
  }, [overview, requests]);
}

/** a submenu's own count while it is closed: its children's, summed (red if any of them is). */
export function sumBadges(badges: Array<NavBadge | undefined>): NavBadge | undefined {
  const bs = badges.filter((b): b is NavBadge => !!b);
  if (!bs.length) return undefined;
  if (bs.length === 1) return bs[0];
  const n = bs.reduce((a, b) => a + b.n, 0);
  return { n, tone: bs.some((b) => b.tone === 'err') ? 'err' : 'info', label: bs.map((b) => b.label).join(', ') };
}

/** the small pill at a row's end; its name ("3 overdue") joins the row's own. */
export function NavCount({ badge, className = '' }: { badge?: NavBadge; className?: string }) {
  if (!badge) return null;
  return (
    <span className={`nav-count nav-count--${badge.tone} ${className}`.trim()} role="img" aria-label={badge.label}>
      {badge.n > 99 ? '99+' : badge.n}
    </span>
  );
}

// ── the rail's glyphs: 24×24, 1.75 stroke, round caps (the comp's icon language) ─────────────────
const GLYPHS: Record<NavIcon, ReactNode> = {
  explore: <><circle cx="12" cy="12" r="8.5" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></>,
  dashboard: <path d="M4 4h6.5v6.5H4zM13.5 4H20v6.5h-6.5zM4 13.5h6.5V20H4zM13.5 13.5H20V20h-6.5z" />,
  agenda: <path d="M4 6.5l1.5 1.5L8 5.5M4 12.5l1.5 1.5L8 11.5M4 18.5l1.5 1.5L8 17.5M11 7h9M11 13h9M11 19h9" />,
  calendar: <path d="M4 6h16v14H4zM4 10.5h16M8.5 3.5v4M15.5 3.5v4M8 14h.01M12 14h.01M16 14h.01M8 17h.01M12 17h.01" />,
  hub: <><circle cx="12" cy="5.5" r="2.2" /><circle cx="5.5" cy="17.5" r="2.2" /><circle cx="18.5" cy="17.5" r="2.2" /><path d="M11 7.5l-4.4 8M13 7.5l4.4 8M7.7 17.5h8.6" /></>,
  delivery: <><path d="M3 6.5h11v9.5H3zM14 9.5h3.6l2.9 3V16H14" /><circle cx="7" cy="17.5" r="1.7" /><circle cx="17" cy="17.5" r="1.7" /></>,
  production: <path d="M4 4h4.5v16H4zM10 4h4.5v10H10zM16 4h4v13h-4z" />,
  people: <><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" /><circle cx="10" cy="8" r="3.5" /><path d="M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.6a3.5 3.5 0 0 1 0 6.8" /></>,
  records: <path d="M3.5 6.5a1 1 0 0 1 1-1H9l2 2.5h8.5a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z" />,
  progress: <path d="M5 20V11M12 20V4.5M19 20v-6.5M3.5 20h17" />,
  tools: <path d="M12 4v16M8 20h8M5 7.5h14M5 7.5l-2.5 6a2.5 2.5 0 0 0 5 0zM19 7.5l-2.5 6a2.5 2.5 0 0 0 5 0z" />,
  checklist: <path d="M9 4h6v3H9zM9 5.5H6V20.5h12V5.5h-3M9 13.5l2 2 4-4" />,
  tag: <path d="M3.5 12V4h8l9 9-8 8zM8 8.2v.1" />,
  layers: <path d="M12 4l8.5 4.5L12 13 3.5 8.5zM3.5 12.5L12 17l8.5-4.5M3.5 16.5L12 21l8.5-4.5" />,
  wrench: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.1-3.1a5.5 5.5 0 0 1-7.3 7.3l-6.4 6.4a2.1 2.1 0 0 1-3-3l6.4-6.4a5.5 5.5 0 0 1 7.3-7.3z" />,
  inbox: <path d="M4 13.5L6.5 5h11l2.5 8.5V19H4zM4 13.5h5l1 2.5h4l1-2.5h5" />,
  document: <path d="M6 3.5h8l4 4v13H6zM14 3.5V8h4M9 12.5h6M9 16h6" />,
  trend: <path d="M3.5 17l5.5-5.5 4 4 7.5-7.5M15 8h5.5v5.5" />,
  contacts: <><path d="M3.5 5.5h17v13h-17z" /><circle cx="9" cy="10.5" r="2.2" /><path d="M5.8 15.6a3.5 3.5 0 0 1 6.4 0M14.5 9.5h3.5M14.5 13h3.5" /></>,
  sheet: <path d="M3.5 5h17v14h-17zM3.5 10h17M3.5 14.5h17M9.5 10v9" />,
  history: <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3L4.5 9M4.5 4.5V9H9M12 8v4l2.8 1.8" />,
  hash: <path d="M9.5 4L7.5 20M16.5 4l-2 16M5 9h15M4 15h15" />,
  receipt: <path d="M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3zM9 8h6M9 11.5h6M9 15h3.5" />,
  box: <path d="M12 3.5l8 4v9l-8 4-8-4v-9zM4 7.5l8 4 8-4M12 11.5v9" />,
  barcode: <path d="M4 5.5v13M7 5.5v13M9.5 5.5v13M13 5.5v13M15.5 5.5v13M18 5.5v13M20.5 5.5v13" />,
  orders: <path d="M8 4.5h11v15H8zM5 7.5v14h11M11 9h5M11 12.5h5" />,
};

export function RailGlyph({ name, size = 18, className }: { name?: NavIcon; size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {name ? GLYPHS[name] : <circle cx="12" cy="12" r="2" />}
    </svg>
  );
}

const Chevron = () => (
  <svg className="rail-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M9.5 6l6 6-6 6" />
  </svg>
);
const ExternalMark = () => (
  <svg className="rail-ext" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M14 4.5h5.5V10M19.5 4.5L11 13M17 13.5v6H4.5V7H10" />
  </svg>
);

/** focus the next / previous rail row (links, disclosure buttons) from a key press inside the rail. */
function moveRailFocus(e: KeyboardEvent<HTMLElement>) {
  const k = e.key;
  if (k !== 'ArrowDown' && k !== 'ArrowUp' && k !== 'Home' && k !== 'End') return;
  const rows = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('.rail-item'));
  if (!rows.length) return;
  const i = rows.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return; // only when a row has focus (the swatches keep their own arrow keys)
  e.preventDefault();
  const j = k === 'Home' ? 0 : k === 'End' ? rows.length - 1
    : Math.max(0, Math.min(rows.length - 1, i + (k === 'ArrowDown' ? 1 : -1)));
  rows[j]!.focus();
}

export function NavRail({ id }: { id?: string }) {
  const { groups, leafVisible } = useNavModel();
  const { pathname } = useLocation();
  const counts = useNavCounts();
  const { uiTheme } = useSettings();
  const scrollRef = useRef<HTMLDivElement>(null);

  // bring the current page's row into view when the page changes (a long rail scrolls on its own)
  useEffect(() => {
    const row = scrollRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    row?.scrollIntoView({ block: 'nearest' });
  }, [pathname]);

  return (
    <nav className="nav-rail" id={id} aria-label="Pages" onKeyDown={moveRailFocus}>
      <div className="rail-scroll" ref={scrollRef}>
        {groups.map(({ group, items }) => (
          <RailGroup key={group.title} title={group.title}>
            {items.map((i) =>
              i.children ? (
                <RailParent key={i.label} item={i} pathname={pathname} leafVisible={leafVisible} counts={counts} />
              ) : (
                <li key={i.path ?? i.external ?? i.label}><RailLink item={i} pathname={pathname} badge={i.path ? counts[i.path] : undefined} /></li>
              ),
            )}
          </RailGroup>
        ))}
      </div>
      {/* a theme with its own colours (Stegi) has no colour profile to pick */}
      {uiTheme.palette === 'full' && <RailSwatches />}
    </nav>
  );
}

function RailGroup({ title, children }: { title: string; children: ReactNode }) {
  const hd = useId();
  return (
    // a named list, not a heading: nine group headings would crowd every page's heading outline
    <div className="rail-group">
      <div className="rail-group-hd" id={hd}>{title}</div>
      <ul className="rail-list" aria-labelledby={hd}>{children}</ul>
    </div>
  );
}

function RailLink({ item, pathname, sub, badge }: { item: NavItem; pathname: string; sub?: boolean; badge?: NavBadge }) {
  const cls = `rail-item${sub ? ' rail-subitem' : ''}`;
  const body = (
    <>
      {!sub && <RailGlyph name={item.icon} className="rail-ic" />}
      <span className="rail-label">{item.label}</span>
      {item.soon && <span className="rail-soon">soon</span>}
      <NavCount badge={badge} />
    </>
  );
  if (item.external) {
    return (
      <a className={cls} href={item.external} target="_blank" rel="noopener noreferrer">
        {body}
        <ExternalMark />
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    );
  }
  const current = pathname === item.path;
  return (
    <Link className={cls} to={item.path!} aria-current={current ? 'page' : undefined}>
      {body}
    </Link>
  );
}

function RailParent({ item, pathname, leafVisible, counts }: {
  item: NavItem;
  pathname: string;
  leafVisible: (i: NavItem) => boolean;
  counts: Record<string, NavBadge>;
}) {
  const children = (item.children ?? []).filter(leafVisible);
  const hasActive = children.some((c) => c.path === pathname);
  const [open, setOpen] = useState(hasActive);
  const subId = useId();
  // arriving on one of its pages from elsewhere (the search, a link) opens the submenu
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);
  return (
    <li>
      <button
        type="button"
        className={`rail-item rail-parent${hasActive ? ' has-current' : ''}`}
        aria-expanded={open}
        aria-controls={subId}
        onClick={() => setOpen((o) => !o)}
      >
        <RailGlyph name={item.icon} className="rail-ic" />
        <span className="rail-label">{item.label}</span>
        {/* closed, the parent carries its children's count; open, each child shows its own */}
        {!open && <NavCount badge={sumBadges(children.map((c) => (c.path ? counts[c.path] : undefined)))} />}
        <Chevron />
      </button>
      <ul className="rail-sub" id={subId} hidden={!open}>
        {children.map((c) => (
          <li key={c.path ?? c.external ?? c.label}><RailLink item={c} pathname={pathname} sub badge={c.path ? counts[c.path] : undefined} /></li>
        ))}
      </ul>
    </li>
  );
}

/** the colour profiles as a radio group of swatches (chrome / accent split), wired to the palette
 *  setting exactly like Settings → Colour theme. Arrow keys move and choose, as in any radio group. */
function RailSwatches() {
  const { prefs, setPalette } = useSettings();
  const current = paletteId(prefs.palette);
  const hd = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'ArrowDown' && k !== 'ArrowUp') return;
    e.preventDefault();
    e.stopPropagation();
    const i = PALETTE_ORDER.indexOf(current);
    const step = k === 'ArrowRight' || k === 'ArrowDown' ? 1 : -1;
    const j = (i + step + PALETTE_ORDER.length) % PALETTE_ORDER.length;
    setPalette(PALETTE_ORDER[j]!);
    refs.current[j]?.focus();
  };
  return (
    <div className="rail-foot">
      <span className="rail-group-hd" id={hd}>Colour</span>
      <div className="rail-swatches" role="radiogroup" aria-labelledby={hd} onKeyDown={onKey}>
        {PALETTE_ORDER.map((id, i) => {
          const p = PALETTES[id];
          const on = id === current;
          return (
            <button
              key={id}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={p.label}
              title={`${p.label} · ${p.blurb}`}
              tabIndex={on ? 0 : -1}
              className="rail-sw"
              style={{ '--sw-a': p.swatch, '--sw-b': p.accent } as CSSProperties}
              onClick={() => setPalette(id)}
            />
          );
        })}
      </div>
    </div>
  );
}
